<script>
import { mapState, mapActions } from 'vuex'
import VueApexCharts from 'vue-apexcharts'

export default {
   components: {
      VueApexCharts,
   },
   data() {
      return {
         // b-tabs menggunakan index numerik sebagai nilai v-model.
         activeTab: 0,
         // Flag loading untuk menampilkan overlay spinner saat request berjalan.
         loading: false,
      }
   },
   computed: {
      ...mapState({
         nibPerStatus: state => state.DashboardOss.nibPerStatus,
         tahun: state => state.DashboardOss.tahun,
         refreshTimestamp: state => state.DashboardOss.refreshTimestamp,
         skin: state => state.appConfig.layout.skin,
      }),
      isDark() {
         return this.skin === 'dark'
      },
      chartData() {
         return this.nibPerStatus.data || {}
      },
      chartSeries() {
         return [
            this.chartData.pma || 0,
            this.chartData.pmdn || 0,
         ]
      },
      chartOptions() {
         const textColor = this.isDark ? '#b4b7bd' : '#6e6b7b'

         return {
            chart: {
               type: 'donut',
               foreColor: textColor,
               toolbar: {
                  show: false,
               },
               fontFamily: 'Montserrat, Helvetica, Arial, sans-serif',
            },
            labels: ['PMA', 'PMDN'],
            colors: ['#7367f0', '#00cfe8'],
            stroke: {
               show: true,
               width: 0,
            },
            plotOptions: {
               pie: {
                  donut: {
                     size: '70%',
                     labels: {
                        show: true,
                        name: {
                           show: true,
                           color: textColor,
                        },
                        value: {
                           show: true,
                           color: textColor,
                           formatter: value => Number(value).toLocaleString('id-ID'),
                        },
                        total: {
                           show: true,
                           showAlways: true,
                           label: 'Total NIB',
                           color: textColor,
                           formatter: () => this.totalAll.toLocaleString('id-ID'),
                        },
                     },
                  },
               },
            },
            dataLabels: {
               enabled: false,
            },
            tooltip: {
               theme: this.isDark ? 'dark' : 'light',
               y: {
                  formatter: value => `${value.toLocaleString('id-ID')} NIB`,
               },
            },
            legend: {
               position: 'bottom',
               labels: {
                  colors: textColor,
               },
            },
         }
      },
      totalPma() {
         return this.chartData.pma || 0
      },
      totalPmdn() {
         return this.chartData.pmdn || 0
      },
      totalAll() {
         return this.chartData.total || 0
      },
   },
   watch: {
      tahun(newYear) {
         this.loadData(newYear)
      },
      activeTab() {
         this.loadData(this.tahun)
      },
      refreshTimestamp() {
         this.loadData(this.tahun)
      },
   },
   mounted() {
      this.loadData(this.tahun)
   },
   methods: {
      ...mapActions({
         getNibPerStatus: 'DashboardOss/getNibPerStatus',
      }),
      async loadData(year) {
         this.loading = true
         try {
            // Ubah index tab BootstrapVue ke nilai yang dipahami oleh action.
            const tab = this.activeTab === 0 || this.activeTab === 'tahun'
               ? 'tahun'
               : 'all_time'
            await this.getNibPerStatus({ year, tab })
         } catch (error) {
            // Chart tetap menampilkan data kosong saat request gagal.
         } finally {
            this.loading = false
         }
      },
   },
}
</script>

<template>
   <b-overlay
      :show="loading"
      :variant="isDark ? 'dark' : 'white'"
      spinner-variant="primary"
      blur="0"
      opacity=".75"
      rounded="sm"
   >
      <b-row>
         <b-col cols="12">
            <b-card class="card-wrapper">
               <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-1">
                  <div class="mb-1 mb-md-0">
                     <h4 class="mb-25">
                        NIB per Status Penanaman Modal
                     </h4>
                     <small class="text-muted">Distribusi NIB berdasarkan PMA dan PMDN</small>
                  </div>
               </div>

               <div class="mb-1">
                  <b-tabs v-model="activeTab" pills>
                     <b-tab :title="`Tahun ${tahun}`" name="tahun" />
                     <b-tab title="All Time" name="all_time" />
                  </b-tabs>
               </div>

               <b-row class="mb-1">
                  <b-col cols="6">
                     <div class="text-center p-1 rounded status-card status-card-primary">
                        <div class="font-weight-bolder status-value">
                           {{ totalPma.toLocaleString('id-ID') }}
                        </div>
                        <small class="text-muted">PMA</small>
                     </div>
                  </b-col>
                  <b-col cols="6">
                     <div class="text-center p-1 rounded status-card status-card-info">
                        <div class="font-weight-bolder status-value">
                           {{ totalPmdn.toLocaleString('id-ID') }}
                        </div>
                        <small class="text-muted">PMDN</small>
                     </div>
                  </b-col>
               </b-row>

               <vue-apex-charts
                  type="donut"
                  height="350"
                  :options="chartOptions"
                  :series="chartSeries"
               />
            </b-card>
         </b-col>
      </b-row>
   </b-overlay>
</template>

<style scoped>
.card-wrapper {
   border: 0;
   border-radius: 12px;
   box-shadow: 0 4px 24px rgba(34, 41, 47, 0.08);
}

.status-card {
   transition: background-color 0.2s ease;
}

.status-card-primary { background-color: #eeedff; }
.status-card-info { background-color: #e0f9fc; }

.status-card-primary .status-value { color: #7367f0; }
.status-card-info .status-value { color: #00cfe8; }

.dark-layout .status-card-primary { background-color: rgba(115, 103, 240, 0.2); }
.dark-layout .status-card-info { background-color: rgba(0, 207, 232, 0.2); }
</style>
